import React, { useState, useEffect } from "react";
import { usePasswordAndUserId } from "../context/AuthContext";
import { getWalletBalance } from "../utils/Wallet";
import { loadLocalKeys } from "../db/KeyStorageManager";
import bs58 from 'bs58';

export default function FundWalletView({ onBack, onNavigatePrivacy }) {
  const {userId, password, salt} = usePasswordAndUserId();
  const [copied, setCopied] = useState(false);
  const [walletAddress, setWalletAddress] = useState("...");
  const [walletBalance, setWalletBalance] = useState(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(walletAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleShowPrivacy = (score, description) => {
    onNavigatePrivacy(score, description);
  };

  useEffect(() => {
    async function loadWalletDetails() {
      try {
        const keys = await loadLocalKeys({userId, password, salt}); 
        const pubKey = bs58.encode(keys.fundingPublicKey);
        const balanceLamports = await getWalletBalance(pubKey);
        const balanceSol = balanceLamports / 1_000_000_000;

        setWalletAddress(pubKey);
        setWalletBalance(balanceSol.toFixed(4));
      } catch (err) {
        console.error("Failed to load funding wallet info", err);
        setWalletBalance("0");
      }
    }

    loadWalletDetails();
  }, [userId, password, salt]);

  const Tile = ({ title, description, children, shields, score, info }) => (
    <div
      style={{
        backgroundColor: "#111",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "1rem",
        marginBottom: "1rem",
        color: "#fff",
      }}
    >
      <h4 style={{ margin: "0 0 0.5rem 0", color: "#4e8cff" }}>{title}</h4>
      <p style={{ marginBottom: "0.75rem", color: "#ccc", fontSize: "0.9rem" }}>{description}</p>
      {children}
      <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "1.5rem" }}>
        <div style={{ display: "flex", gap: "0.25rem", cursor: "pointer" }} onClick={() => handleShowPrivacy(score, info)}>
          {Array.from({ length: shields }).map((_, i) => (
            <img
              key={i}
              src="/images/shield-icon.png"
              alt="Shield"
              style={{
                height: "18px",
                width: "18px",
                filter: "invert(47%) sepia(96%) saturate(1946%) hue-rotate(202deg) brightness(102%) contrast(97%)",
              }}
            />
          ))}
        </div>
        <button
          onClick={() => handleShowPrivacy(score, info)}
          style={{
            background: "none",
            color: "#4e8cff",
            border: "none",
            padding: "0 0 0 5px",
            fontSize: "0.85rem",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Why this score?
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: "#000", color: "#fff", height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Sticky Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#000",
          borderBottom: "1px solid #222",
          padding: "1rem",
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          onClick={onBack}
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            marginRight: "1rem",
            cursor: "pointer",
          }}
        >
          ←
        </button>
        <h2 style={{ margin: 0 }}>Fund Your Wallet</h2>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          padding: "2rem 1.5rem",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
        className="hide-scroll"
      >
        <p style={{ color: "#ccc", marginBottom: "1rem" }}>
          <strong>Unlink runs on Solana, a decentralized network.</strong><br />
          To send messages, your funding wallet needs a small amount of SOL (Solana’s currency) to cover network fees. Receiving messages is free.<br /><br />
          Your current funding wallet balance: <strong>{walletBalance ?? "..."}</strong> SOL.<br /><br />
        </p>

        {/* Address Box */}
        <div
          onClick={handleCopy}
          style={{
            backgroundColor: "#111",
            color: "#4e8cff",
            fontSize: "1.05rem",
            padding: "1rem 0.75rem 0.75rem",
            borderRadius: "6px",
            border: "1px solid #4e8cff",
            fontFamily: "monospace",
            cursor: "pointer",
            position: "relative",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ position: "absolute", top: "0.5rem", left: "0.75rem", fontSize: "0.8rem", color: "#4e8cff" }}>
            Your Unlink funding address
          </div>
          <div style={{ marginTop: "1.25rem", wordBreak: "break-all" }}>{walletAddress}</div>
          <span
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "0.5rem",
              color: "#4e8cff",
              fontSize: "0.8rem",
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </span>
        </div>

        <p style={{ color: "#888", fontSize: "0.85rem", marginBottom: "2rem" }}>
          <em>Note: this is your wallet for funding only. It's different from your Unlink address used to receive messages.</em>
        </p>

        {/* Options */}
        <Tile
          title="Option 1: Get SOL fast – ready in 60 seconds"
          description="Use a service like Ramp to buy SOL and deposit it directly to your Unlink funding address. It's fast and hassle-free."
          shields={3}
          score="Protected"
          info="Using Ramp is convenient and still safe — while Ramp, or anyone else, cannot see who you’re messaging or what your messages contain, your wallet address will be linked to personal information like your name or payment method. It’s a good option for most users, but if you want to keep your wallet more anonymous, a non-KYC funding method is the better choice."
        >
          <button
            style={{
              backgroundColor: "#4e8cff",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1rem",
              fontSize: "0.95rem",
              fontWeight: "bold",
              cursor: "pointer",
              width: "100%",
              maxWidth: "200px",
              marginTop: "0.75rem",
            }}
          >
            Buy with Ramp
          </button>
        </Tile>
        <Tile
          title="Option 2: Send from another wallet - for maximum privacy"
          description="Already have SOL or getting it from a friend? Sending it directly to your Unlink funding address is the most private and secure way to get started."
          shields={5}
          score="Maximum Privacy"
          info="Sending from a wallet you control offers the highest level of privacy — especially if that wallet isn’t linked to you or anyone you know. Even if a link exists, no one can read your messages or see who you’re communicating with. This is the recommended way to use the Unlink protocol."
        />

        <Tile
          title="Option 3: Use an exchange"
          description="Buy SOL on an exchange like Coinbase or Binance, then send it to your funding address."
          shields={4}
          score="Hardened Privacy"
          info="Centralized exchanges typically require KYC, linking your identity to your wallet. Your messages and contacts are always private on Unlink. Even if someone could see you’re using the app and sending messages, they’d never know what you’re saying or who you’re talking to. Using a non-KYC exchange is the preferred way to fund your wallet, as it makes it harder to connect your wallet to your real identity."
        >
          <ul style={{ paddingLeft: "1.2rem", marginTop: "0.5rem", marginBottom: 0, fontSize: "0.9rem" }}>
            <li>
              <a href="https://www.coinbase.com" target="_blank" rel="noreferrer" style={{ color: "#4e8cff" }}>
                Coinbase
              </a>
            </li>
            <li>
              <a href="https://www.kraken.com" target="_blank" rel="noreferrer" style={{ color: "#4e8cff" }}>
                Kraken
              </a>
            </li>
            <li>
              <a href="https://www.binance.com" target="_blank" rel="noreferrer" style={{ color: "#4e8cff" }}>
                Binance
              </a>
            </li>
            <li>
              <a href="https://www.weex.com" target="_blank" rel="noreferrer" style={{ color: "#4e8cff" }}>
                Weex (non-KYC)
              </a>
            </li>
            <li>
              <a href="https://www.coinex.com" target="_blank" rel="noreferrer" style={{ color: "#4e8cff" }}>
                Coinex (non-KYC)
              </a>
            </li>
          </ul>
        </Tile>
      </div>
    </div>
  );
}